// services/estatisticasService.ts
import { apiClient } from '../config/api';
import { ApiResponse } from '../types/api';

// Types para estatísticas de produção
export interface MetaComissaoMensalStats {
  mesAno: string;
  premioLiquido: number;
  percentualComissao: number;
  valorComissao: number;
  metaAtiva: boolean;
}

export interface DocumentosRealizadosStats {
  mesAno: string;
  premioLiquidoRealizado: number;
  comissaoRealizada: number;
  quantidadeDocumentos: number;
  dataUltimaAtualizacao: string;
}

export interface ProducaoMensalResponse {
  metaMensal: {
    id: string;
    mesAno: string;
    valorPremio: number;
    valorComissao: number;
    percentualBase: number;
    realizado: {
      premioLiquido: number;
      comissao: number;
      quantidadeDocumentos: number;
    };
    percentualAtingimento: {
      premioLiquido: number;
      comissao: number;
    };
  };
  data: ProducaoItem[];
}

export interface ProducaoItem {
  mesAno: string;
  produto: string;
  metaProduto: {
    id: string;
    percentualParticipacao: number | null;
    valorObjetivoVenda: number | null;
  };
  objetivoMensal: {
    premioLiquido: number;
    comissao: number;
  };
  totalAteHoje: {
    premioLiquido: number;
    comissao: number;
    quantidadeDocumentos: number;
  };
  necessarioDiario: {
    premioLiquido: number;
    comissao: number;
    diasRestantes: number;
  };
  realizadoInicioVigencia: {
    totalPremioLiquido: number;
    totalComissao: number;
    quantidadeDocumentos: number;
    documentos: Array<{
      id: string;
      numeroDocumento: string;
      inicioVigencia: string;
      premioLiquido: number;
      comissao: number;
    }>;
  };
  realizadoFimVigencia: {
    totalPremioLiquido: number;
    totalComissao: number;
    quantidadeDocumentos: number;
    documentos: any[];
  };
  analise: {
    atingimentoInicioVigencia: number | null;
    atingimentoFimVigencia: number | null;
    produtosEncontrados: string[];
  };
}

export interface AcompanhamentoMensalStats {
  // Dados da meta
  meta: {
    id: string;
    mesAno: string;
    valorPremio: number;
    valorComissao: number;
    percentualBase: number;
    empresa?: {
      id: string;
      nome: string;
    };
  };
  
  // Dados realizados no mês
  realizadoMes: {
    mesAno: string;
    totalPremioLiquido: number;
    totalComissao: number;
    quantidadeDocumentos: number;
    documentos: any[];
  };
  
  // Total faltante para atingir meta
  totalFaltante: {
    premioLiquido: number;
    comissao: number;
  };
  
  // Percentual atingido (valores de 0 a 100)
  percentualAtingido: {
    premioLiquido: number;
    comissao: number;
  };
  
  // Dias restantes no mês
  diasRestantesMes: number | null;
  
  // Média diária necessária
  necessarioDiario: {
    premioLiquido: number | null;
    comissao: number | null;
  };
}

class EstatisticasService {
  private baseUrl = '/estatisticas/metas';

  // ✅ GET /api/estatisticas/metas/comissao-mensal/mes/:mesAno
  async getMetaMensal(mesAno: string): Promise<ApiResponse<MetaComissaoMensalStats>> {
    const mesAnoFormatado = EstatisticasService.formatMesAnoParaAPI(mesAno);
    return apiClient.request<ApiResponse<MetaComissaoMensalStats>>(
      `${this.baseUrl}/comissao-mensal/mes/${mesAnoFormatado}`
    );
  }

  // ✅ GET /api/estatisticas/metas/documentos/inicio-vigencia/:mesAno
  async getDocumentosRealizados(mesAno: string): Promise<ApiResponse<DocumentosRealizadosStats>> {
    const mesAnoFormatado = EstatisticasService.formatMesAnoParaAPI(mesAno);
    return apiClient.request<ApiResponse<DocumentosRealizadosStats>>(
      `${this.baseUrl}/documentos/inicio-vigencia/${mesAnoFormatado}`
    );
  }

  // ✅ GET /api/estatisticas/metas/acompanhamento/:mesAno
  async getAcompanhamentoMensal(mesAno: string): Promise<ApiResponse<AcompanhamentoMensalStats>> {
    const mesAnoFormatado = EstatisticasService.formatMesAnoParaAPI(mesAno);
    return apiClient.request<ApiResponse<AcompanhamentoMensalStats>>(
      `${this.baseUrl}/acompanhamento/${mesAnoFormatado}`
    );
  }

  // ✅ GET /api/estatisticas/metas/produto-comissao/:mesAno/comparacao
  async getProducaoMensal(mesAno: string): Promise<ApiResponse<ProducaoMensalResponse>> {
    const mesAnoFormatado = EstatisticasService.formatMesAnoParaAPI(mesAno);
    return apiClient.request<ApiResponse<ProducaoMensalResponse>>(
      `${this.baseUrl}/produto-comissao/${mesAnoFormatado}/comparacao`
    );
  }

  // 🔧 Utilitário para formatar mês/ano atual
  static getMesAnoAtual(): string {
    const agora = new Date();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const ano = agora.getFullYear();
    return `${mes}/${ano}`;
  }

  // 🔧 Utilitário para converter MM/YYYY para MMYYYY (formato da API)
  static formatMesAnoParaAPI(mesAno: string): string {
    // Se já está no formato MMYYYY, retorna como está
    if (mesAno.length === 6 && !mesAno.includes('/')) {
      return mesAno;
    }
    
    // Converte MM/YYYY para MMYYYY
    return mesAno.replace('/', '');
  }

  // 🔧 Utilitário para calcular dias úteis restantes no mês
  static calcularDiasUteisRestantes(mesAno: string): number {
    const [mes, ano] = mesAno.split('/').map(Number);
    const hoje = new Date();
    const ultimoDiaMes = new Date(ano, mes, 0).getDate();
    
    let diasUteis = 0;
    for (let dia = hoje.getDate(); dia <= ultimoDiaMes; dia++) {
      const data = new Date(ano, mes - 1, dia);
      const diaSemana = data.getDay();
      // Contar apenas dias úteis (segunda a sexta: 1-5)
      if (diaSemana >= 1 && diaSemana <= 5) {
        diasUteis++;
      }
    }
    
    return diasUteis;
  }

  // 🔧 Método agregado para buscar todos os dados do card produção
  async getDadosCardProducao(mesAno?: string): Promise<{
    meta: MetaComissaoMensalStats | null;
    documentos: DocumentosRealizadosStats | null;
    acompanhamento: AcompanhamentoMensalStats | null;
  }> {
    const mesAnoConsulta = mesAno || EstatisticasService.getMesAnoAtual();
    
    try {
      const [metaResponse, documentosResponse, acompanhamentoResponse] = await Promise.allSettled([
        this.getMetaMensal(mesAnoConsulta),
        this.getDocumentosRealizados(mesAnoConsulta),
        this.getAcompanhamentoMensal(mesAnoConsulta)
      ]);

      return {
        meta: metaResponse.status === 'fulfilled' ? metaResponse.value.data : null,
        documentos: documentosResponse.status === 'fulfilled' ? documentosResponse.value.data : null,
        acompanhamento: acompanhamentoResponse.status === 'fulfilled' ? acompanhamentoResponse.value.data : null
      };
    } catch (error) {
      console.error('Erro ao buscar dados do card produção:', error);
      return {
        meta: null,
        documentos: null,
        acompanhamento: null
      };
    }
  }
}

// ✅ Exportar instância
export const estatisticasService = new EstatisticasService();
export { EstatisticasService };